/**
 * Hariom ERP - Demand Forecast State Store (Zustand)
 *
 * Holds the full contractor list in ONE place and persists it to the browser
 * (localStorage). Because this lives outside the page component, the data is
 * NOT lost when the user navigates to another page (e.g. Dashboard) and comes
 * back, and it also survives a full page refresh.
 *
 * This is what fixes the "imported 110 contractors but it drops back to 15"
 * bug — that happened because the list used to live in component state, which
 * resets every time the Demand Forecast page re-mounts.
 */
import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'
import type { Contractor } from '../types'
import { sharedStorage } from './sharedStorage'

interface ForecastState {
  /** The single source of truth for every contractor row. */
  contractors: Contractor[]
  /**
   * Which seed version has been loaded. When the bundled SEED_VERSION is newer
   * than this, the page loads the real company data once (then leaves it alone,
   * so the user's edits/imports are never overwritten afterwards).
   */
  seedVersion: number

  /** Replace the whole list (used by Excel import and by add/edit helpers). */
  setContractors: (list: Contractor[]) => void
  /** Append one new contractor. */
  addContractor: (c: Contractor) => void
  /** Patch a single contractor by id. */
  updateContractor: (id: string, updated: Contractor) => void
  /** Record which seed version is currently loaded. */
  setSeedVersion: (v: number) => void
}

export const useForecastStore = create<ForecastState>()(
  persist(
    (set) => ({
      contractors: [],
      seedVersion: 0,

      setContractors: (list) => set({ contractors: list }),

      addContractor: (c) =>
        set((state) => ({ contractors: [...state.contractors, c] })),

      updateContractor: (id, updated) =>
        set((state) => ({
          contractors: state.contractors.map((c) => (c.id === id ? updated : c)),
        })),

      setSeedVersion: (v) => set({ seedVersion: v }),
    }),
    {
      name: 'hariom-erp-forecast',
      storage: createJSONStorage(() => sharedStorage), // shared across devices
      partialize: (state) => ({
        contractors: state.contractors,
        seedVersion: state.seedVersion,
      }),
    }
  )
)
